import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {ClubCaddie} from "../../../api/ClubCaddieService";
import {Review, ReviewSearchStatisticsResource} from "../../../api/ReviewService";
import {loadClubCaddies, loadNextPageReviews, loadReviews, loadStatistics} from "./async_actions";

interface State {
    filterSidePanel: {
        open: boolean
    },
    caddies: {
        all: ClubCaddie[],
        filtered: ClubCaddie[],
        errMsg?: string
    }
    filters: {
        caddie: ClubCaddie[],
        stars: number[],
        date: {
            from: string
            to: string
        }
    },
    reviewFeed: {
        nextPage: number,
        atMax: boolean,
        loading: boolean,
        errMsg?: string
        reviews: Review[]
    },
    stats?: ReviewSearchStatisticsResource
}

const initialState: State = {
    filterSidePanel: {
        open: false,
    },
    caddies: {
        all: [],
        filtered: []
    },
    filters: {
        caddie: [],
        stars: [],
        date: {
            from: toDateString(new Date(new Date().getTime() - (31 * 24 * 60 * 60 * 1000))),
            to: toDateString(new Date())
        }
    },
    reviewFeed: {
        nextPage: 0,
        atMax: false,
        loading: false,
        reviews: [],
    }
};

export type LoadedReviewsPayload = {
    errMsg?: string
    result?: {
        page: number
        atMax: boolean
        reviews: Review[]
    }
}

export type LoadedStatsPayload = {
    errMsg?: string
    result?: ReviewSearchStatisticsResource
}

export type LoadedCaddiesPayload = {
    errMsg?: string
    caddies: ClubCaddie[],
}

function toDateString(date: Date) {
    return date.toISOString().split("T")[0];
}

let slice = createSlice(
    {
        name: "Reviews",
        initialState: initialState,
        reducers: {
            handleFilterSidePanelOpen: (state, action: PayloadAction<boolean>) => {
                state.filterSidePanel.open = action.payload
            },
            handleToggleStarFilter: (state, action: PayloadAction<number>) => {
                let exists = state.filters.stars.includes(action.payload)
                if (exists) {
                    state.filters.stars = state.filters.stars.filter(s => s != action.payload)
                    return;
                }

                state.filters.stars.push(action.payload)
            },
            handlePresetDateFilterSelected: (state, action: PayloadAction<{ from: Date, to: Date }>) => {
                state.filters.date.from = toDateString(action.payload.from)
                state.filters.date.to = toDateString(action.payload.to)
            },
            handleCustomFromDateSelected: (state, action: PayloadAction<string>) => {
                const date = new Date(action.payload);
                if (!isNaN(date.getTime())) {
                    state.filters.date.from = toDateString(date)
                }
            },
            handleCustomToDateSelected: (state, action: PayloadAction<string>) => {
                const date = new Date(action.payload);
                if (!isNaN(date.getTime())) {
                    state.filters.date.to = toDateString(date)
                }
            },
            handleSetLoadingReviewFeed: (state, action: PayloadAction<boolean>) => {
                state.reviewFeed.loading = action.payload
            },
            handleSetReviewFeedErrMsg: (state, action: PayloadAction<string | undefined>) => {
                state.reviewFeed.errMsg = action.payload
            },
            handleFilterCaddies: (state, action: PayloadAction<string | undefined>) => {
                let search = action.payload;
                if (!search) {
                    state.caddies.filtered = state.caddies.all
                    return;
                }

                state.caddies.filtered = state.caddies.all
                    .filter(c => c.displayName
                        .toLowerCase()
                        .includes(search?.toLowerCase() ?? ''))
            },
            handleToggleCaddieFilter: (state, action: PayloadAction<ClubCaddie>) => {
                let exists = state.filters.caddie.map(s => s.caddieId).includes(action.payload.caddieId)
                if (exists) {
                    state.filters.caddie = state.filters.caddie.filter(s => s.caddieId != action.payload.caddieId)
                    return;
                }

                state.filters.caddie.push(action.payload)
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(loadReviews.pending, (state) => {
                    state.reviewFeed.loading = true;
                    state.reviewFeed.reviews = [];
                })
                .addCase(loadReviews.fulfilled, (state, action: PayloadAction<LoadedReviewsPayload>) => {
                    state.reviewFeed.errMsg = action.payload.errMsg;

                    if (!action.payload.result) {
                        return;
                    }

                    state.reviewFeed.reviews = action.payload.result.reviews;
                    state.reviewFeed.nextPage = action.payload.result.page;
                    state.reviewFeed.atMax = action.payload.result.atMax;
                })
                .addCase(loadNextPageReviews.fulfilled, (state, action: PayloadAction<LoadedReviewsPayload>) => {
                    state.reviewFeed.errMsg = action.payload.errMsg;

                    if (!action.payload.result) {
                        return;
                    }

                    state.reviewFeed.reviews = [
                        ...state.reviewFeed.reviews,
                        ...action.payload.result.reviews
                    ];
                    state.reviewFeed.nextPage = action.payload.result.page;
                    state.reviewFeed.atMax = action.payload.result.atMax;
                })
                .addCase(loadClubCaddies.fulfilled, (state, action: PayloadAction<LoadedCaddiesPayload>) => {
                    state.caddies.errMsg = action.payload.errMsg;
                    state.caddies.all = action.payload.caddies;
                    state.caddies.filtered = action.payload.caddies;
                })
                .addCase(loadStatistics.fulfilled, (state, action: PayloadAction<LoadedStatsPayload>) => {
                    state.stats = action.payload.result
                })
        }
    }
);

export const {
    handleFilterSidePanelOpen,
    handleToggleStarFilter,
    handlePresetDateFilterSelected,
    handleCustomFromDateSelected,
    handleCustomToDateSelected,
    handleSetLoadingReviewFeed,
    handleSetReviewFeedErrMsg,
    handleFilterCaddies,
    handleToggleCaddieFilter,
} = slice.actions;

export default slice.reducer;